/**
 * Vault Section Constants — maps RBAC permissions to encrypted vault sections.
 * Used for per-section encryption where each section has its own AES-256-GCM key.
 */

export const VAULT_SECTIONS = [
  'script',
  'characters',
  'storyboard',
  'locations',
  'scenes',
  'budget',
  'schedule',
  'greenlight',
];

// Maps each RBAC permission to the vault sections it grants access to
export const PERMISSION_SECTION_MAP = {
  view_all:          ['script', 'characters', 'storyboard', 'locations', 'scenes', 'budget', 'schedule', 'greenlight'],
  edit_script:       ['script', 'scenes'],
  generate_shots:    ['storyboard'],
  manage_characters: ['characters'],
  manage_costumes:   ['characters'],
  manage_locations:  ['locations'],
  shot_blocking:     ['storyboard'],
  budget_scheduling: ['budget', 'schedule'],
  invite_team:       [],
};

// Maps each section to the project data fields it contains
export const SECTION_DATA_MAP = {
  script:     ['script', 'script_content', 'formatted_content'],
  characters: ['characters', 'character_sheets', 'costumes'],
  storyboard: ['storyboard_frames', 'shot_anchors', 'shot_list'],
  locations:  ['locations', 'location_designs'],
  scenes:     ['scenes', 'scene_breakdowns'],
  budget:     ['budget', 'budget_items'],
  schedule:   ['schedule', 'shooting_days'],
  greenlight: ['greenlight_analysis', 'greenlight_history'],
};

/**
 * Given a permissions object (from RBAC), return the set of vault sections the user can access.
 */
export function getAccessibleSections(permissions) {
  const sections = new Set();
  for (const [perm, allowed] of Object.entries(permissions)) {
    if (allowed && PERMISSION_SECTION_MAP[perm]) {
      for (const section of PERMISSION_SECTION_MAP[perm]) {
        sections.add(section);
      }
    }
  }
  return [...sections];
}

/**
 * Given a permissions object, return sections the user can WRITE to (not just view).
 * view_all grants read-only access — it does NOT grant write.
 */
export function getWritableSections(permissions) {
  const sections = new Set();
  for (const [perm, allowed] of Object.entries(permissions)) {
    if (allowed && perm !== 'view_all' && PERMISSION_SECTION_MAP[perm]) {
      for (const section of PERMISSION_SECTION_MAP[perm]) {
        sections.add(section);
      }
    }
  }
  return [...sections];
}
